import { setupSplitScreen } from '@gotti/test-utils';
import type { ClientPlaygroundConfigOptions, PlaygroundLayout } from '@gotti/test-utils';

interface Cell { container: HTMLElement; canvas: HTMLCanvasElement }

/** One full-size viewport over the whole target; a client's canvas is `canvas<index>`. */
const singleCell = (target: HTMLElement, index: number): Cell => {
  const container = document.createElement('div');
  container.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;overflow:hidden;';
  const canvas = document.createElement('canvas');
  canvas.id = `canvas${index}`;
  canvas.width = target.clientWidth || window.innerWidth || 1280;
  canvas.height = target.clientHeight || window.innerHeight || 720;
  canvas.style.cssText = 'width:100%;height:100%;display:block;';
  container.appendChild(canvas);
  target.appendChild(container);
  return { container, canvas };
};

/**
 * The default `domSetup` for `count` clients: one full-size canvas, or a tiled grid built with
 * `setupSplitScreen`, whose `'grid'` cells keep `aspectRatio` (width / height; default 16:9).
 * Cells are created on the first call so the target has laid out.
 */
export const createLayoutDomSetup = (
  target: HTMLElement,
  count: number,
  layout: PlaygroundLayout,
  aspectRatio?: number,
): NonNullable<ClientPlaygroundConfigOptions['domSetup']> => {
  let cells: Cell[] | null = null;
  return (clientIndex) => {
    if (!cells) {
      if (layout === 'single' || count <= 1) {
        cells = Array.from({ length: Math.max(1, count) }, (_, index) => singleCell(target, index));
      } else {
        const { canvases, wrappers } = setupSplitScreen({ container: target, count, arrangement: layout === 'stack' ? 'stack' : 'grid', aspectRatio });
        cells = canvases.map((canvas, i) => ({ canvas, container: wrappers[i] }));
      }
    }
    const cell = cells[clientIndex];
    if (!cell) throw new Error(`[gotti][playground] layout "${layout}" prepared ${cells.length} canvas(es) but client ${clientIndex} asked for one — set clientConfig.numberOfClients.`);
    return { container: cell.container, canvas: cell.canvas };
  };
};
